import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowPathIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/solid';

import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { downloadReport } from '../utils/downloadReport';

const currentYear = new Date().getFullYear();
const currentMonth = String(new Date().getMonth() + 1);

const monthOptions = [
  ['1', 'Enero'],
  ['2', 'Febrero'],
  ['3', 'Marzo'],
  ['4', 'Abril'],
  ['5', 'Mayo'],
  ['6', 'Junio'],
  ['7', 'Julio'],
  ['8', 'Agosto'],
  ['9', 'Septiembre'],
  ['10', 'Octubre'],
  ['11', 'Noviembre'],
  ['12', 'Diciembre'],
];

const tabs = [
  { key: 'consolidado', label: 'Consolidado' },
  { key: 'tramites_pendientes_cobro', label: 'Tramites pendientes' },
  { key: 'tramites_no_justificados', label: 'No justificados' },
  { key: 'pagos_extras_pendientes', label: 'Extras pendientes' },
  { key: 'creditos_pendientes_cobro', label: 'Creditos por cobrar' },
];

const toMoney = (value) => {
  const number = Number(value);
  return `$${Number.isFinite(number) ? number.toFixed(2) : '0.00'}`;
};

const AdminCollectionsSummary = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [matrizadores, setMatrizadores] = useState([]);
  const [activeTab, setActiveTab] = useState('consolidado');
  const [filters, setFilters] = useState({
    period: 'month',
    date: '',
    year: String(currentYear),
    month: currentMonth,
    start_date: '',
    end_date: '',
    usuario_matrizador: '',
  });

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) {
      toast.error('No tienes permisos para acceder al resumen administrativo.');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const buildParams = useCallback(() => {
    const params = { period: filters.period };
    if (filters.usuario_matrizador) params.usuario_matrizador = filters.usuario_matrizador;

    if (filters.period === 'day') {
      if (filters.date) params.date = filters.date;
      return params;
    }
    if (filters.period === 'range') {
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      return params;
    }
    params.year = filters.year;
    params.month = filters.month;
    return params;
  }, [filters]);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/audit/reports/admin-collections-summary/', {
        params: buildParams(),
      });
      setSummary(response.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'No se pudo cargar el resumen administrativo.');
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (!loading && user?.is_admin) {
      fetchSummary();
    }
  }, [loading, user, fetchSummary]);

  useEffect(() => {
    const fetchMatrizadores = async () => {
      try {
        const response = await api.get('/api/audit/matrizadores/list/');
        const rows = Array.isArray(response.data) ? response.data : [];
        setMatrizadores(rows.map((row) => String(row?.nombre || '').trim()).filter(Boolean));
      } catch (error) {
        setMatrizadores([]);
      }
    };

    if (!loading && user?.is_admin) {
      fetchMatrizadores();
    }
  }, [loading, user]);

  const matrizadorOptions = useMemo(() => {
    const values = [
      ...matrizadores,
      ...(Array.isArray(summary?.by_matrizador) ? summary.by_matrizador.map((row) => row.label) : []),
    ];
    return Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [matrizadores, summary]);

  const detailRows = useMemo(() => {
    const rows = Array.isArray(summary?.rows) ? summary.rows : [];
    if (activeTab === 'consolidado') return rows;
    return rows.filter((row) => row.category === activeTab);
  }, [summary, activeTab]);

  const handleDownload = () => {
    downloadReport({
      api,
      endpoint: '/api/audit/reports/admin-collections-summary/',
      params: buildParams(),
      format: 'xlsx',
      baseName: 'resumen-admin-cobros',
    });
  };

  if (loading) return <div className="text-white text-center">Cargando...</div>;
  if (!user) return null;

  const totals = summary?.totals || {};

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-gray-900 p-4 text-white sm:p-6">
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />

      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Notaria 4 Canton Otavalo" className="w-12 h-auto sm:w-14" />
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">Resumen Administrativo de Cobros</h1>
            <p className="text-sm text-gray-300">{summary?.period?.label || 'Periodo seleccionado'}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={fetchSummary}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3"
          >
            <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!summary}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white disabled:opacity-50"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            Descargar XLSX
          </button>
          <Link to="/dashboard/notario" className="rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3">
            Volver
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <select
              value={filters.period}
              onChange={(e) => setFilters((prev) => ({ ...prev, period: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Periodo"
            >
              <option value="month">Mes</option>
              <option value="day">Dia</option>
              <option value="range">Rango</option>
            </select>

            {filters.period === 'day' && (
              <input
                type="date"
                value={filters.date}
                onChange={(e) => setFilters((prev) => ({ ...prev, date: e.target.value }))}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                aria-label="Dia"
              />
            )}

            {filters.period === 'month' && (
              <>
                <input
                  type="number"
                  value={filters.year}
                  onChange={(e) => setFilters((prev) => ({ ...prev, year: e.target.value }))}
                  className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                  aria-label="Anio"
                />
                <select
                  value={filters.month}
                  onChange={(e) => setFilters((prev) => ({ ...prev, month: e.target.value }))}
                  className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                  aria-label="Mes"
                >
                  {monthOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </>
            )}

            {filters.period === 'range' && (
              <>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
                  className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                  aria-label="Desde"
                />
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
                  className="rounded-lg bg-notaria-blue-2 p-2 text-white"
                  aria-label="Hasta"
                />
              </>
            )}

            <select
              value={filters.usuario_matrizador}
              onChange={(e) => setFilters((prev) => ({ ...prev, usuario_matrizador: e.target.value }))}
              className="rounded-lg bg-notaria-blue-2 p-2 text-white"
              aria-label="Matrizador"
            >
              <option value="">Todos los matrizadores</option>
              {matrizadorOptions.map((nombre) => (
                <option key={nombre} value={nombre}>{nombre}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={fetchSummary}
              className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white"
            >
              Aplicar
            </button>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <MetricCard icon={<BanknotesIcon className="h-6 w-6" />} label="Total abierto" value={toMoney(totals.total_abierto)} count="Sin duplicar" />
          <MetricCard label="Tramites pendientes" value={toMoney(totals.tramites_pendientes_cobro?.total)} count={`${totals.tramites_pendientes_cobro?.count || 0} registros`} />
          <MetricCard label="No justificados" value={toMoney(totals.tramites_no_justificados?.total)} count={`${totals.tramites_no_justificados?.count || 0} registros`} />
          <MetricCard label="Extras pendientes" value={toMoney(totals.pagos_extras_pendientes?.total)} count={`${totals.pagos_extras_pendientes?.count || 0} registros`} />
          <MetricCard label="Creditos por cobrar" value={toMoney(totals.creditos_pendientes_cobro?.total)} count={`${totals.creditos_pendientes_cobro?.count || 0} registros`} />
        </section>

        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="mb-3 flex items-center gap-2 text-lg font-semibold">
            <CalendarDaysIcon className="h-5 w-5 text-notaria-yellow" />
            Consolidado por matrizador
          </div>
          <SummaryTable rows={summary?.by_matrizador || []} emptyText="No hay matrizadores con valores pendientes." />
        </section>

        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl">
          <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-5" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg px-3 py-2 text-sm font-bold transition ${
                  activeTab === tab.key ? 'bg-notaria-yellow text-notaria-blue-1' : 'bg-notaria-blue-2/70 text-white hover:bg-notaria-blue-3'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'consolidado' ? (
            <SummaryTable rows={summary?.by_day || []} emptyText="No hay dias con valores pendientes." />
          ) : (
            <DetailTable rows={detailRows} isLoading={isLoading} />
          )}
        </section>
      </main>
    </motion.div>
  );
};

const MetricCard = ({ icon, label, value, count }) => (
  <div className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-lg">
    <div className="mb-2 flex items-center justify-between gap-3">
      <p className="text-sm text-gray-300">{label}</p>
      {icon || null}
    </div>
    <p className="text-2xl font-bold text-notaria-yellow">{value}</p>
    <p className="text-sm text-gray-300">{count}</p>
  </div>
);

const SummaryTable = ({ rows, emptyText }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-notaria-blue-2 text-left">
          <th className="p-3">Nombre</th>
          <th className="p-3">Total abierto</th>
          <th className="p-3">Tramites</th>
          <th className="p-3">No justificados</th>
          <th className="p-3">Extras</th>
          <th className="p-3">Creditos</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan="6" className="p-4 text-center text-gray-400">{emptyText}</td></tr>
        ) : rows.map((row) => (
          <tr key={row.key} className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2/40">
            <td className="p-3 font-semibold">{row.label}</td>
            <td className="p-3 font-semibold text-notaria-yellow">{toMoney(row.total_abierto)}</td>
            <td className="p-3">{row.tramites_pendientes_cobro?.count || 0} / {toMoney(row.tramites_pendientes_cobro?.total)}</td>
            <td className="p-3">{row.tramites_no_justificados?.count || 0} / {toMoney(row.tramites_no_justificados?.total)}</td>
            <td className="p-3">{row.pagos_extras_pendientes?.count || 0} / {toMoney(row.pagos_extras_pendientes?.total)}</td>
            <td className="p-3">{row.creditos_pendientes_cobro?.count || 0} / {toMoney(row.creditos_pendientes_cobro?.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DetailTable = ({ rows, isLoading }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-notaria-blue-2 text-left">
          <th className="p-3">Dia</th>
          <th className="p-3">Matrizador</th>
          <th className="p-3">Referencia</th>
          <th className="p-3">Estado</th>
          <th className="p-3">Monto</th>
          <th className="p-3">Detalle</th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr><td colSpan="6" className="p-4 text-center text-gray-300">Cargando...</td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan="6" className="p-4 text-center text-gray-400">No hay registros en esta vista.</td></tr>
        ) : rows.map((row, index) => (
          <tr key={`${row.category}-${row.source_type}-${row.source_id}-${index}`} className="border-b border-notaria-blue-2 hover:bg-notaria-blue-2/40">
            <td className="p-3">{row.date}</td>
            <td className="p-3">{row.matrizador || '-'}</td>
            <td className="p-3">{row.reference || '-'}</td>
            <td className="p-3">{row.status || '-'}</td>
            <td className="p-3 font-semibold">{toMoney(row.amount)}</td>
            <td className="p-3">
              <div className="max-h-20 max-w-xl overflow-y-auto text-xs text-gray-200">
                {typeof row.description === 'string' ? row.description : JSON.stringify(row.description || {})}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default AdminCollectionsSummary;
